import React, { useEffect, useRef, useState } from "react";
import { X, Send, Paperclip, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MoonAvatar from "@/components/MoonAvatar";

const SESSION_KEY = "moon_chat_session";
const HISTORY_KEY = "moon_chat_history";

function newSessionId() {
  return "moon-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
}

const GREETING = "Hi, I'm Moon — your personal stylist at Star Jewels. Looking for a gift, styling advice for an outfit, or help with a piece you've received? I'm here for you.";

export default function MoonChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return [{ role: "assistant", text: GREETING }];
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]); // { path, url } uploaded photos pending
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) { s = newSessionId(); localStorage.setItem(SESSION_KEY, s); }
    return s;
  });
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-50))); }, [messages]);
  useEffect(() => { if (open) scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text && photos.length === 0) return;
    // Compose message text — if photos attached, tell Moon about them.
    let userMsg = text;
    if (photos.length > 0) userMsg += (text ? "\n\n" : "") + `[Attached ${photos.length} photo${photos.length > 1 ? "s" : ""}]`;
    setMessages((m) => [...m, { role: "user", text: userMsg, photos: photos.map((p) => p.url) }]);
    setInput("");
    const attachedPhotos = photos;
    setPhotos([]);
    setBusy(true);
    try {
      const { data } = await api.post("/moon/chat", { session_id: sessionId, message: userMsg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      // Heuristic: if photos attached OR user mentioned defective/broken/damaged/exchange,
      // log an exchange request so admin can review.
      const looksLikeExchange = /broken|damag|defect|exchang|refund|return|not working|cracked/i.test(text);
      if (attachedPhotos.length > 0 || looksLikeExchange) {
        try {
          await api.post("/moon/exchange-request", {
            session_id: sessionId,
            description: text || "(photos attached)",
            photos: attachedPhotos.map((p) => p.path),
          });
        } catch {}
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "I'm briefly unable to reach the boutique — please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files.slice(0, 2)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/moon/exchange-request/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = URL.createObjectURL(f);
        setPhotos((p) => [...p, { path: data.image, url }]);
      } catch {
        toast.error("Photo upload failed");
      }
    }
  };

  return (
    <>
      {/* Floating trigger — visible on all pages, bottom-right */}
      <button
        onClick={() => setOpen(true)}
        className="fixed z-[100] bottom-5 right-5 sm:bottom-6 sm:right-6 h-14 w-14 rounded-full bg-white shadow-xl border border-sj-gold/40 hover:shadow-2xl transition-shadow flex items-center justify-center overflow-hidden"
        aria-label="Chat with Moon"
        data-testid="moon-chat-trigger"
      >
        <MoonAvatar size={52} />
      </button>

      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:right-6 sm:bottom-6 sm:w-[380px] sm:h-[560px] z-[110] bg-white shadow-2xl sm:rounded-2xl overflow-hidden flex flex-col border border-sj-ink/10" data-testid="moon-chat-panel">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-sj-ink/10 bg-sj-cream/60">
            <MoonAvatar size={36} />
            <div className="flex-1 min-w-0">
              <div className="font-display text-base leading-tight">Chat with Moon</div>
              <div className="text-[11px] text-sj-inkSoft">Personal stylist · Star Jewels</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-sj-ink/5" aria-label="Close" data-testid="moon-chat-close"><X size={16} /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="moon-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-2 items-end`}>
                {m.role === "assistant" && <MoonAvatar size={24} />}
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-sj-ink text-white rounded-br-sm" : "bg-sj-cream text-sj-ink rounded-bl-sm"}`}>
                  {m.text}
                  {m.photos && m.photos.length > 0 && (
                    <div className="mt-2 flex gap-1.5 flex-wrap">
                      {m.photos.map((u, k) => <img key={k} src={u} alt="" className="h-14 w-14 object-cover rounded" />)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-end gap-2">
                <MoonAvatar size={24} />
                <div className="bg-sj-cream text-sj-inkSoft rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm italic">Moon is composing a reply…</div>
              </div>
            )}
          </div>

          {/* Pending photo attachments preview */}
          {photos.length > 0 && (
            <div className="px-4 py-2 flex gap-2 border-t border-sj-ink/10 bg-sj-cream/40">
              {photos.map((p, i) => (
                // Using p.url as key so removing a photo re-uses the surviving
                // DOM nodes instead of shifting subsequent indices (which would
                // otherwise carry across the wrong image previews).
                <div key={p.url} className="relative">
                  <img src={p.url} alt="" className="h-12 w-12 object-cover rounded" />
                  <button onClick={() => setPhotos((arr) => arr.filter((_, k) => k !== i))} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white shadow border border-sj-ink/20 flex items-center justify-center"><X size={9} /></button>
                </div>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-sj-ink/10 p-3 flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-sj-ink/5 text-sj-inkSoft" aria-label="Attach photo" data-testid="moon-chat-attach">
              <Paperclip size={16} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickFile} />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask Moon anything…"
              className="flex-1 h-10 px-3 rounded-full bg-sj-cream text-sm outline-none focus:ring-2 focus:ring-sj-gold/30"
              data-testid="moon-chat-input"
              disabled={busy}
            />
            <button onClick={send} disabled={busy} className="h-10 w-10 rounded-full bg-sj-ink text-white flex items-center justify-center disabled:opacity-60" aria-label="Send" data-testid="moon-chat-send">
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
